<template>
  <TwcButton
    v-if="!isOtherPaymentSelected"
    class="w-full h-12 text-xl bg-seastreak-action-blue hover:bg-seastreak-action-blue-light"
    data-testid="other-payment-button"
    @click="setOtherPaymentAsOpen"
  >
    Other Payment
  </TwcButton>
  <div v-if="isOtherPaymentSelected" ref="otherPaymentFields">
    <div
      :class="`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2 ${errorMessage ? 'border-red-500' : ''}`"
    >
      <div ref="ccInput"></div>
    </div>
    <div v-if="errorMessage" class="text-sm text-red-600 dark:text-red-500 mt-1">
      {{ errorMessage }}
    </div>
    <div class="mt-2">
      <TwcInput
        v-if="!isAuthenticated"
        v-model="email"
        placeholder="Email"
        class="!text-base"
        type="email"
        data-testid="email-input"
        size="sm"
        :validation-status="isValidEmail ? undefined : 'error'"
      />
    </div>
    <p v-if="!isValidEmail" class="text-sm text-red-600 dark:text-red-500 mt-1">
      Your email address is invalid
    </p>
    <div v-if="!isAuthenticated" class="mt-2">
      <TwcPhoneInput
        placeholder="Mobile phone (optional)"
        :ip-info-key="ipInfoKey"
        :display-error="showInputPhoneError"
        data-testid="phone-input"
        @change="onPhoneChanged"
      />
      <p v-if="showInputPhoneError" class="text-sm text-red-600 dark:text-red-500 mt-1">
        Your phone is invalid
      </p>
    </div>
    <TwcButton
      class="w-full h-12 text-xl bg-seastreak-action-blue hover:bg-seastreak-action-blue-light mt-2"
      data-testid="button-submit-payment"
      @click="submit"
    >
      Submit Payment
    </TwcButton>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { TwcButton, TwcPhoneInput } from 'transit-web-components'
import TwcInput from '@/components/web-components/TwcInput/TwcInput.vue'
import { useStripeStore } from '@/stores/stripe'
import { useOrderStore } from '@/stores/order'
import { createOrder } from '@/services/api/orders'
import { requestPaymentIntent } from '@/services/api/payments'
import validators from '@/utils/validators'
import { TICKETS_ROUTES } from '@/router'
import { useAuthStore } from '@/stores/auth'
import { useCustomerStore } from '@/stores/customer'
import { getError } from '@/utils/errors'
import { useGoogleTagManagerStore } from '@/stores/google-tag-manager'

const ccInput = ref(null)
const email = ref('')
const phoneNumber = ref('')
const errorMessage = ref()
const isValidEmail = ref(true)
const isValidPhone = ref(true)
const showInputPhoneError = ref(false)
const stripeCardElement = ref()
const stripeElements = ref()
const stripeStore = useStripeStore()
const router = useRouter()
const orderStore = useOrderStore()
const authStore = useAuthStore()
const customerStore = useCustomerStore()
const otherPaymentFields = ref()
const { isLoading: isStripeLoading } = storeToRefs(stripeStore)
const isOtherPaymentSelected = ref(
  !isStripeLoading.value &&
    !stripeStore.isApplePayAvailable &&
    !stripeStore.isGooglePayAvailable &&
    !stripeStore.setupApplePay &&
    !orderStore.hasPrimaryCard
)
const { isAuthenticated } = storeToRefs(authStore)
const ipInfoKey = import.meta.env.VITE_IP_INFO_KEY

async function setOtherPaymentAsOpen() {
  isOtherPaymentSelected.value = true
  await nextTick()

  if (otherPaymentFields.value) {
    otherPaymentFields.value.scrollTop = 0
  }
}

async function submit() {
  errorMessage.value = null
  if (validateOtherPayment()) {
    orderStore.isProcessing = true

    if (isAuthenticated.value) {
      orderStore.customerId = customerStore.id
    } else {
      orderStore.customerEmail = email.value
      orderStore.customerPhone = phoneNumber.value
    }

    const card = stripeElements.value.getElement('card')
    const resultIntent = await requestPaymentIntent({ card })

    if (!resultIntent.error) {
      const resultOrder = await createOrder(resultIntent.paymentIntentId)

      if (!resultOrder.error) {
        orderStore.updateFromResponse(resultOrder)
        useGoogleTagManagerStore().trackOrderCompleted(orderStore.id, orderStore.total)
        router.push({ name: TICKETS_ROUTES.ORDER_CONFIRMATION, params: { id: orderStore.id } })
      } else {
        errorMessage.value = getError(resultOrder)
        orderStore.isProcessing = false
      }
    } else {
      errorMessage.value = getError(resultIntent)
      orderStore.isProcessing = false
    }
  }
}

function setupCCInput() {
  if (!isStripeLoading.value && ccInput.value) {
    stripeElements.value = stripeStore.stripe.elements()
    stripeCardElement.value = stripeElements.value.create('card', {
      hidePostalCode: true,
      style: { base: { fontSize: '16px' } }
    })
    stripeCardElement.value.mount(ccInput.value)
  }
}

function validateOtherPayment() {
  if (!isAuthenticated.value) {
    isValidEmail.value = validators.isEmailValid(email.value)
    showInputPhoneError.value = !isValidPhone.value
    return isValidEmail.value && isValidPhone.value
  }
  return true
}

function onPhoneChanged(value: string, isValid: boolean, valueE164: string) {
  isValidPhone.value = isValid || value.toString().trim().length == 0
  if (isValid) {
    phoneNumber.value = valueE164
    showInputPhoneError.value = false
  } else {
    phoneNumber.value = ''
  }
}

watch(isStripeLoading, () => {
  setupCCInput()
  isOtherPaymentSelected.value =
    !isStripeLoading.value &&
    !stripeStore.isApplePayAvailable &&
    stripeStore.isGooglePayAvailable &&
    stripeStore.setupApplePay &&
    !orderStore.hasPrimaryCard
})

watch(ccInput, setupCCInput)

watch(
  () => orderStore.hasPrimaryCard,
  () => {
    isOtherPaymentSelected.value = isOtherPaymentSelected.value && !orderStore.hasPrimaryCard
  }
)

setupCCInput()
</script>

<style></style>
